import _ from "lodash";
import React, { useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";

import { Modal, Button } from "@toddle-edu/ds-web";
import DualPanelManageMembers from "AppComponents/DualPanelManageMembers";
import DualPanelManageMembersV2 from "AppComponents/DualPanelManageMembersV2";
import { DUAL_PANEL_VIEW_MODE } from "AppComponents/DualPanelManageMembersV2/module/utils";
import {
  addMembersInChannel,
  addMembersInDepartmentChannel,
} from "MessagingV2/modules/MessagingModule";
import { useI18n } from "Hooks";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import UsersAndCohortsQueryWrapper, {
  metadataProps as usersAndCohortsMetadataProps,
} from "MessagingV2/components/Channels/AddMembersV3/UsersAndCohortsQueryWrapper";
import DepartmentChannelQueryWrapper, {
  metadataProps as departmentChannelMetadataProps,
} from "MessagingV2/components/Channels/AddMembersV3/DepartmentChannelQueryWrapper";
import {
  CHAT_TYPE_ENUM,
  getUserTypeOption,
  isMessagingCurriculumWiseSplitEnabled,
  isDualPanelRevampEnabled,
} from "MessagingV2/modules/Utils";
import { getDualPanelQueryWrapperConfig } from "MessagingV2/components/DualPanelQueryWrapper";
import Filter, {
  FILTER_KEYS,
} from "MessagingV2/components/Channels/CreateChannelModal/Filter";
import classes from "./ManageMembersModal.scss";

const styles = {
  addMemberModalBodyStyles: {
    padding: "0px",
    overflow: "hidden",
  },
  modalBodyStyles: {
    paddingTop: "4px",
    paddingRight: "0px",
    overflow: "hidden",
  },

  modalHeaderStyle: {
    padding: "16px 24px",
    marginBottom: 0,
  },
};

const SHORTCUT_CODE = {
  ENTER: { code: "Enter", shiftKey: true },
};

const overrideClasses = {
  usersViewContainer: classes.usersViewContainer,
};

const getHeaderTitle = ({ channelType, channelLabel, t }) => {
  switch (channelType) {
    case CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT:
      return t("common:add_other_family_member_to_label", {
        label: channelLabel,
      });
    default:
      return t("common:manage_members_for_label", { label: channelLabel });
  }
};

const AddMembersModal = props => {
  const {
    onCloseModal,
    prevUsers,
    chatId,
    onBackClick,
    addMembersInChannel,
    addMembersInDepartmentChannel,
    channelLabel,
    channelType,
  } = props;

  const { t } = useI18n(["common"]);

  const [selectedAddUsers, setSelectedAddUsers] = useState([]);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [typeFilter, setTypeFilter] = useState(DUAL_PANEL_VIEW_MODE.MEMBERS);
  const [selectedFilters, setSelectedFilters] = useState({
    [FILTER_KEYS.SCHOOL]: [],
    [FILTER_KEYS.USER_TYPE]: [],
  });

  const customQueryWrapperFields = useMemo(
    () => ({ typeFilter, selectedFilters }),
    [typeFilter, selectedFilters]
  );

  const isDepartmentChannel = _.isEqual(
    channelType,
    CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT
  );

  const handleAddUsers = users => {
    setSelectedAddUsers(state => {
      const userList = _.map(users, user => _.pick(user, ["id", "type"]));
      return _.uniqBy([...state, ...userList], "id");
    });
  };

  const handleRemoveUsers = users => {
    const userIdsSet = new Set(_.map(users, "id"));
    setSelectedAddUsers(prev =>
      _.filter(prev, user => !userIdsSet.has(user.id))
    );
  };

  const handleUpdateUsers = async () => {
    setIsSubmitButtonDisabled(true);
    const asyncQuery = isDepartmentChannel
      ? addMembersInDepartmentChannel
      : addMembersInChannel;
    const isSuccess = await asyncQuery({
      chatId,
      users: _.concat(selectedAddUsers, prevUsers),
    });
    if (isSuccess) {
      setSelectedAddUsers([]);
    }
    setIsSubmitButtonDisabled(false);
    onCloseModal(false);
  };

  const legacyMetadataProps = isDepartmentChannel
    ? departmentChannelMetadataProps
    : usersAndCohortsMetadataProps;

  const LegacyQueryWrapper = isDepartmentChannel
    ? DepartmentChannelQueryWrapper
    : UsersAndCohortsQueryWrapper;

  const { metadata, renderQueryWrapper } = useMemo(
    getDualPanelQueryWrapperConfig,
    []
  );

  const renderLegacyQueryWrapper = useCallback(
    queryProps => (
      <LegacyQueryWrapper
        includePaidUsersOnly={isMessagingCurriculumWiseSplitEnabled()}
        {...queryProps}
      />
    ),
    [LegacyQueryWrapper]
  );

  const renderBody = () => {
    if (!isDepartmentChannel && isDualPanelRevampEnabled()) {
      return (
        <DualPanelManageMembersV2
          prevUsers={prevUsers}
          handleAddUsers={handleAddUsers}
          handleRemoveUsers={handleRemoveUsers}
          metadataProps={metadata}
          renderQueryWrapper={renderQueryWrapper}
          overrideClasses={overrideClasses}
          filterComponent={
            <Filter
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              selectedFilters={selectedFilters}
              onFiltersChange={setSelectedFilters}
            />
          }
          customQueryWrapperFields={customQueryWrapperFields}
        />
      );
    }

    return (
      <DualPanelManageMembers
        handleAddUsers={handleAddUsers}
        handleRemoveUsers={handleRemoveUsers}
        prevUsers={prevUsers}
        metadataProps={legacyMetadataProps}
        dsVersion="2.0"
        searchPlaceholder="common:search_member_placeholder"
        showTypeFilter
        typeFilterOptions={getUserTypeOption({ t })}
        showSearchAndFilter={!isDepartmentChannel}
        renderQueryWrapper={renderLegacyQueryWrapper}
        overrideClasses={overrideClasses}
      />
    );
  };

  const getIsButtonDisabled = () =>
    _.isEmpty(selectedAddUsers) || isSubmitButtonDisabled;

  return (
    <Modal
      dsVersion="2.0"
      width="1000px"
      height="680px"
      isClose={false}
      onClose={onCloseModal}
    >
      <Modal.Header isClosable hasSeparator style={styles.modalHeaderStyle}>
        {getHeaderTitle({ channelType, channelLabel, t })}
      </Modal.Header>
      <Modal.Body style={styles.addMemberModalBodyStyles}>
        {renderBody()}
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.modalButtonContainer}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            onClick={onBackClick}
          >
            {t("common:back")}
          </Button>
          <Button
            variant="progressive"
            onClick={handleUpdateUsers}
            disabled={getIsButtonDisabled()}
            dsVersion="2.0"
            shortcut={SHORTCUT_CODE.ENTER}
          >
            {t("common:add_members")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapActionCreators = {
  addMembersInChannel,
  addMembersInDepartmentChannel,
  setToastMsg,
};

export default connect(null, mapActionCreators)(AddMembersModal);
